import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  path?: string;
  keywords?: string[];
  datePublished?: string;
  dateModified?: string;
  author?: string;
  category?: string;
  price?: string;
  serviceArea?: string[];
}

const defaultImage = 'https://i.imgur.com/jsSwFcY.jpg';
const siteUrl = 'https://www.hometheaternashvilletn.com';
const defaultKeywords = [
  'home theater',
  'audio installation',
  'video installation',
  'custom home theater',
  'home automation',
  'surround sound',
  'Nashville',
  'Middle Tennessee',
  'AV installation',
  'theater design',
  'smart home',
  'home entertainment'
];

const defaultServiceAreas = [
  'Nashville',
  'Brentwood',
  'Franklin',
  'Gallatin',
  'Mount Juliet',
  'Middle Tennessee'
];

export default function SEO({
  title,
  description,
  image = defaultImage,
  type = 'website',
  path = '',
  keywords = [],
  datePublished,
  dateModified,
  author,
  category,
  price,
  serviceArea = defaultServiceAreas
}: SEOProps) {
  const siteTitle = 'Home Theater Nashville TN';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'Expert audio/video installation and maintenance services for homes and businesses in Middle Tennessee. Professional installation, maintenance, repair, and upgrades for all your A/V needs.';
  const metaDescription = description || defaultDescription;
  const url = `${siteUrl}${path}`;
  const allKeywords = [...defaultKeywords, ...keywords].join(', ');

  // Base schema for the organization
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': siteUrl,
    name: 'Pinnacle Home Theater',
    image: defaultImage,
    url: siteUrl,
    telephone: '(615) 506-3451',
    email: 'pinnaclehometheater@hotmail.com',
    priceRange: price || '$$',
    description: defaultDescription,
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'TN',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 36.1627,
        longitude: -86.7816
      },
      geoRadius: '50000'
    },
    areaServed: serviceArea,
    founder: {
      '@type': 'Person',
      name: 'Nathan Moss'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00'
      }
    ],
    sameAs: [
      'https://facebook.com/pinnaclehometheater',
      'https://instagram.com/pinnaclehometheater',
      'https://linkedin.com/company/pinnaclehometheater'
    ]
  };

  // Schema for service pages
  const serviceSchema = type === 'service' ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': url,
    name: title,
    description: description,
    provider: {
      '@type': 'LocalBusiness',
      '@id': siteUrl
    },
    areaServed: serviceArea,
    category: category || 'Home Theater Installation',
    image: image,
    url: url
  } : null;

  // Schema for blog posts
  const blogPostSchema = type === 'article' ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': url,
    url,
    headline: title,
    image: image,
    articleBody: description,
    datePublished: datePublished || new Date().toISOString(),
    dateModified: dateModified || new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: author || 'Pinnacle Home Theater'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pinnacle Home Theater',
      logo: {
        '@type': 'ImageObject',
        url: defaultImage
      }
    },
    articleSection: category,
    keywords: keywords.join(', ')
  } : null;

  // Schema for gallery items
  const galleryItemSchema = type === 'ImageGallery' ? {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    '@id': url,
    url,
    name: title,
    description: description,
    image: image,
    datePublished: datePublished || new Date().toISOString(),
    dateModified: dateModified || new Date().toISOString(),
    about: {
      '@type': 'Thing',
      name: category || 'Home Theater Installation'
    }
  } : null;

  // Schema for contact page
  const contactSchema = type === 'ContactPage' ? {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': url,
    name: title,
    description: description,
    url: url,
    provider: {
      '@type': 'LocalBusiness',
      '@id': siteUrl
    }
  } : null;

  // Combine all schemas
  const schemas = [
    organizationSchema,
    serviceSchema,
    blogPostSchema,
    galleryItemSchema,
    contactSchema
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content={author || 'Pinnacle Home Theater'} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#010e1e" />
      <meta name="geo.region" content="US-TN" />
      <meta name="geo.placename" content="Nashville" />
      <meta name="geo.position" content="36.1627;-86.7816" />
      <meta name="ICBM" content="36.1627, -86.7816" />

      {/* Open Graph Tags */}
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="en_US" />
      <meta property="business:contact_data:street_address" content="Nashville" />
      <meta property="business:contact_data:locality" content="Nashville" />
      <meta property="business:contact_data:region" content="TN" />
      <meta property="business:contact_data:postal_code" content="37201" />
      <meta property="business:contact_data:country_name" content="United States" />

      {/* Article-specific Open Graph tags */}
      {type === 'article' && datePublished && (
        <>
          <meta property="article:published_time" content={datePublished} />
          {dateModified && (
            <meta property="article:modified_time" content={dateModified} />
          )}
          {author && (
            <meta property="article:author" content={author} />
          )}
          {category && (
            <meta property="article:section" content={category} />
          )}
        </>
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* Link Tags */}
      <link rel="canonical" href={url} />
      <link rel="icon" href="/theater.svg" />
      <link rel="apple-touch-icon" href="/theater.svg" />

      {/* Preconnect to Important Domains */}
      <link rel="preconnect" href="https://bcyjuqgufhhqleggtkhl.supabase.co" />
      <link rel="dns-prefetch" href="https://bcyjuqgufhhqleggtkhl.supabase.co" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      <link rel="dns-prefetch" href="https://images.unsplash.com" />
      
      {/* Preload critical resources */}
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Montserrat:wght@500;600;700;800&display=swap" as="style" />
      <link rel="preload" href="/theater.svg" as="image" />

      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}